import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const fmtWhen = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const Check = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer select-none">
    <input type="checkbox" className="w-5 h-5 accent-[#1E6B45]" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
    {label}
  </label>
);

function TransferRow({ t, onChange }) {
  const [note, setNote] = useState(t.note || "");
  const patch = async (body) => {
    try { const { data } = await api.patch(`/admin/transfers/${t.id}`, body); onChange(data); return true; }
    catch (e) { toast.error(apiError(e)); return false; }
  };
  return (
    <li className="card p-5" data-testid={`transfer-row-${t.id}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="font-display text-lg font-bold">{t.club}</p>
        <p className="text-sm text-[#5B665F]">{t.name}</p>
        <span className={`pill ml-auto ${t.payment_received ? "bg-[#DFF3E6] text-[#145A36]" : t.invoice_sent ? "bg-[#E3EBFA] text-[#1F4396]" : "bg-[#FDEBD3] text-[#8A4B00]"}`}>
          {t.payment_received ? "Payé" : t.invoice_sent ? "Facture envoyée" : "Facture à envoyer"}
        </span>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm">
        <a href={`mailto:${encodeURIComponent(t.email)}`} className="link">{t.email}</a>
        <a href={`tel:${encodeURIComponent(t.phone.replace(/[^\d+]/g, ""))}`} className="link">{t.phone}</a>
        <span className="text-[#5B665F]" data-testid={`transfer-date-${t.id}`}>Demande du {fmtWhen(t.created_at)}</span>
      </div>
      <p className="text-sm text-[#2B3830] mt-2 whitespace-pre-line" data-testid={`transfer-address-${t.id}`}>{t.address}</p>
      <div className="flex flex-wrap gap-6 mt-4">
        <Check label="Facture envoyée" checked={t.invoice_sent} onChange={(v) => patch({ invoice_sent: v })} testId={`transfer-invoice-${t.id}`} />
        <Check label="Paiement reçu" checked={t.payment_received} onChange={(v) => patch({ payment_received: v })} testId={`transfer-paid-${t.id}`} />
      </div>
      {t.payment_received && (
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <button className="btn-primary btn-sm" onClick={async () => { try { const { data } = await api.post(`/admin/transfers/${t.id}/send-invite`); onChange(data); toast.success(`Lien de création envoyé à ${t.email}`); } catch (e) { toast.error(apiError(e)); } }} data-testid={`transfer-send-invite-${t.id}`}>Envoyer le lien de création</button>
          {t.invite_sent_at && <span className="text-xs text-[#5B665F]" data-testid={`transfer-invite-sent-${t.id}`}>Lien envoyé le {fmtWhen(t.invite_sent_at)}</span>}
        </div>
      )}
      <textarea className="field h-auto min-h-[70px] py-2 mt-3" placeholder="Note" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`transfer-note-${t.id}`} />
      {note !== (t.note || "") && (
        <button className="btn-primary btn-sm mt-2" onClick={async () => (await patch({ note })) && toast.success("Note enregistrée")} data-testid={`transfer-note-save-${t.id}`}>Enregistrer la note</button>
      )}
    </li>
  );
}

export default function AdminTransfers() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/admin/transfers").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const onChange = (doc) => setList((l) => l.map((x) => (x.id === doc.id ? doc : x)));
  return (
    <section data-testid="admin-transfers">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Virements</h1>
      <p className="text-sm text-[#5B665F] mt-2" data-testid="admin-transfers-count">{list ? `${list.length} demande${list.length > 1 ? "s" : ""}` : "Chargement…"}</p>
      <ul className="space-y-3 mt-8">
        {list?.map((t) => <TransferRow key={t.id} t={t} onChange={onChange} />)}
        {list?.length === 0 && <li className="card p-6 text-sm text-[#5B665F]" data-testid="admin-transfers-empty">Aucune demande pour l'instant.</li>}
      </ul>
    </section>
  );
}
